/**
 * issue.js — Issue lifecycle management
 *
 * Handles the data transformations that happen when an issue changes state:
 *   - Accept:  apply replacement, shift sibling offsets, mark overlaps, recover stale
 *   - Dismiss: mark rejected (handled inline — no helper needed)
 *   - Revert:  undo replacement, shift siblings back (handled in StyleGuideChecker)
 *
 * IMPORTANT: When an edit changes paragraph text, ALL issues in that paragraph
 * with offsets AFTER the edit must be shifted by the delta (suggested.length - original.length).
 * This applies to both open AND accepted issues — otherwise accepted issues get
 * stale offsets that corrupt the document on revert.
 */

import { buildAnchor } from './highlight';

/**
 * Update all issues after one issue is accepted.
 *
 * Produces a new issues array with:
 *   1. The accepted issue: status='accepted', end updated, anchorAccepted set
 *   2. Overlapping open issues in the same paragraph: marked 'stale'
 *   3. Non-overlapping issues after the edit (open OR accepted): offsets shifted by delta
 *   4. Stale issues: attempt recovery near expected shifted position
 *
 * @param {Array} allIssues      - Current issues array
 * @param {Object} acceptedIssue - The issue being accepted (with current start/end)
 * @param {Array} paragraphsAfter - Paragraphs parsed from the post-edit XML
 * @returns {Array} Updated issues array
 */
export function updateIssuesAfterAccept(allIssues, acceptedIssue, paragraphsAfter) {
  // Character shift introduced by replacing original -> suggested.
  const delta = acceptedIssue.suggested.length - acceptedIssue.original.length;
  // Start offset of the accepted issue in its paragraph (pre-shift baseline).
  const aStart = acceptedIssue.start;
  // End offset of the accepted issue in its paragraph (pre-shift baseline).
  const aEnd = acceptedIssue.end;
  // New end after replacement length change.
  const acceptedNewEnd = aStart + acceptedIssue.suggested.length;

  // Build quick paragraphId -> text lookup for anchor rebuilding.
  const pTextMap = new Map();
  // Record each paragraph's latest text after XML mutation.
  for (const p of paragraphsAfter) pTextMap.set(p.paragraphId, p.text || '');

  // Text for the paragraph where acceptance happened.
  const pText = pTextMap.get(acceptedIssue.paragraphId) || '';

  // Phase 1: Update statuses/offsets in one pass over all issues.
  const updated = allIssues.map((it) => {
    // Case 1: the accepted issue itself gets status + accepted anchor.
    if (it.id === acceptedIssue.id) {
      return {
        // Preserve original fields not explicitly changed below.
        ...it,
        // Accepted by user in this operation.
        status: 'accepted',
        // Accepted text length may differ from original length.
        end: acceptedNewEnd,
        // Build anchor on the new accepted text range.
        anchorAccepted: buildAnchor(pText, aStart, acceptedNewEnd, acceptedIssue.suggested),
      };
    }

    // Case 2: different paragraph: offsets unaffected by this local edit.
    if (it.paragraphId !== acceptedIssue.paragraphId) return it;

    // Case 3: rejected/stale remain unchanged here.
    if (it.status === 'rejected' || it.status === 'stale') return it;

    // Case 4: open issues in same paragraph require overlap/shift checks.
    if (it.status === 'open') {
      // Overlap check against accepted pre-edit span.
      const overlaps = !(it.end <= aStart || it.start >= aEnd);
      // Any overlap means this issue's range is no longer trustworthy.
      if (overlaps) return { ...it, status: 'stale' };

      // Shift open issues that come after the edit
      if (it.start >= aEnd) {
        // Move start by delta caused by accepted replacement.
        const newStart = it.start + delta;
        // Move end by same delta.
        const newEnd = it.end + delta;
        return {
          // Keep existing metadata where still valid.
          ...it,
          // Persist shifted start.
          start: newStart,
          // Persist shifted end.
          end: newEnd,
          // Recompute open anchor at shifted location.
          anchorOpen: buildAnchor(pText, newStart, newEnd, it.original),
        };
      }
      // Open issue before accepted range stays unchanged.
      return it;
    }

    // Case 5: accepted issues after the edit also need shifting (for safe revert).
    if (it.status === 'accepted' && it.start >= aEnd) {
      // Shift accepted issue start.
      const newStart = it.start + delta;
      // Shift accepted issue end.
      const newEnd = it.end + delta;
      return {
        // Preserve other fields.
        ...it,
        // Store shifted start.
        start: newStart,
        // Store shifted end.
        end: newEnd,
        // Recompute accepted anchor at shifted location.
        anchorAccepted: buildAnchor(pText, newStart, newEnd, it.suggested),
      };
    }

    // Any unaffected case falls through unchanged.
    return it;
  });

  // Phase 2: Try to recover stale issues.
  // Search near the expected shifted position (not from 0) to avoid
  // first-occurrence ambiguity with duplicate text.
  return updated.map((it) => {
    // Only attempt recovery for stale items.
    if (it.status !== 'stale') return it;

    // Current paragraph text where stale issue might still exist.
    const paraText = pTextMap.get(it.paragraphId) || '';

    // Search near where we expect the text to have shifted
    // Expected stale start after applying delta from accepted issue.
    const expectedStart = Math.max(0, it.start + delta);
    // Limit search window to nearby area for better disambiguation.
    const searchFrom = Math.max(0, expectedStart - 50);
    // Find original phrase near expected location.
    const nearIdx = paraText.indexOf(it.original, searchFrom);

    // If not found nearby, keep stale.
    if (nearIdx === -1) return it;

    // Verify recovered position doesn't overlap the just-accepted range
    // Candidate recovered end for original phrase.
    const nearEnd = nearIdx + it.original.length;
    // Prevent recovery into text that is now owned by accepted replacement.
    const overlapsAccepted = !(nearEnd <= aStart || nearIdx >= acceptedNewEnd);
    // Unsafe recovery; leave stale.
    if (overlapsAccepted) return it;

    // Recovery succeeded: reopen issue with corrected offsets/anchor.
    return {
      ...it,
      status: 'open',
      start: nearIdx,
      end: nearEnd,
      anchorOpen: buildAnchor(paraText, nearIdx, nearEnd, it.original),
    };
  });
}

/**
 * Rebuild anchorOpen for all open issues, with text verification.
 *
 * Verifies the text at [start, end) still matches issue.original.
 * If it drifted, attempts a nearby search to recover the correct offset.
 * If recovery fails, nulls out the anchor so highlighting skips it.
 *
 * @param {Array} issues     - Current issues array
 * @param {Array} paragraphs - Current paragraphs
 * @returns {Array} Issues with fresh anchorOpen values
 */
export function rebuildOpenAnchors(issues, paragraphs) {
  // Build paragraph text lookup for cheap random access.
  const pTextMap = new Map(paragraphs.map((p) => [p.paragraphId, p.text || '']));

  return issues.map((it) => {
    // Only open issues get anchorOpen regeneration.
    if (it.status !== 'open') return it;

    // Paragraph text for this issue.
    const t = pTextMap.get(it.paragraphId) || '';
    // Prefer stored end; derive fallback from original length.
    const end = it.end ?? it.start + (it.original?.length || 0);

    // Verify text at [start, end) still matches
    // Current substring at expected offsets.
    const currentSlice = t.slice(it.start, end);
    // Fast path: offsets still valid.
    if (currentSlice === it.original) {
      return {
        ...it,
        anchorOpen: buildAnchor(t, it.start, end, it.original),
      };
    }

    // Text drifted — try nearby search to recover
    // Search close to expected start to avoid wrong duplicate matches.
    const searchFrom = Math.max(0, it.start - 100);
    // Locate original phrase near prior offset.
    const nearIdx = t.indexOf(it.original, searchFrom);
    // Accept recovery only if reasonably close.
    if (nearIdx !== -1 && Math.abs(nearIdx - it.start) <= 100) {
      // Compute recovered end.
      const newEnd = nearIdx + it.original.length;
      return {
        ...it,
        start: nearIdx,
        end: newEnd,
        anchorOpen: buildAnchor(t, nearIdx, newEnd, it.original),
      };
    }

    // Can't find it — null anchor so highlighting skips gracefully
    console.warn(
      `[rebuildOpenAnchors] Text mismatch for issue ${it.id}: expected "${it.original?.slice(0, 30)}" at [${it.start}:${end}]`,
    );
    // Preserve issue but disable highlight anchor.
    return { ...it, anchorOpen: null };
  });
}

/**
 * Add anchor metadata to freshly computed issues.
 * Called once after computeOffsets resolves the Gemini response.
 *
 * @param {Array} issues     - Issues from computeOffsets (have start/end but no anchors)
 * @param {Array} paragraphs - Original-doc paragraphs
 * @returns {Array} Issues with anchorOpen set, anchorAccepted null
 */
export function initializeIssueAnchors(issues, paragraphs) {
  // Build paragraph text lookup once for all issues.
  const pTextMap = new Map(paragraphs.map((p) => [p.paragraphId, p.text || '']));

  return issues.map((issue) => {
    // Paragraph text where this issue was found.
    const t = pTextMap.get(issue.paragraphId) || '';
    return {
      // Keep all original issue metadata from offset computation.
      ...issue,
      // Fresh issues begin as "open", so only anchorOpen is populated.
      anchorOpen: buildAnchor(t, issue.start, issue.end, issue.original),
      // Accepted anchor starts empty until user accepts.
      anchorAccepted: null,
    };
  });
}
